import { amplitudeService } from '../services/amplitude';

/**
 * Initialize Amplitude with configuration from global BFG object
 */
export const initializeAmplitude = (): void => {
  // Check if Amplitude is enabled and configured
  // Use BFG.amplitude if available, otherwise fallback to BF.amplitude (for login page)
  const amplitudeConfig = window.BFG?.amplitude || window.BF?.amplitude;


  if (!amplitudeConfig?.enabled || !amplitudeConfig?.apiKey) {
    return;
  }

  // Initialize Amplitude
  amplitudeService.init(amplitudeConfig.apiKey);

  // Set user if available (not available on login page)
  if (window.BFG?.currentUser?.user_id) {
    const userProperties = {
      email: window.BFG.currentUser.email,
      company: window.BFG.currentUser.company,
      role: window.BFG.currentUser.role,
      device: window.BFG.currentUser.device,
      environment: amplitudeConfig.environment,
      // Plan feature flags for targeting
      has_people_tagging: !!window.BFG.hasFeature?.('people_tagging'),
      // Brandfolder-level settings (if in a brandfolder context)
      brandfolder_people_tagging_enabled: !!window.BFG.brandfolderSettings?.people_tagging,
    };

    amplitudeService.setUser(window.BFG.currentUser.user_id, userProperties);
  }

  // Expose amplitudeService globally for inline scripts
  window.amplitudeService = amplitudeService;
};

/**
 * Track page view when route changes (for SPA behavior)
 */
export const trackPageView = (pageName?: string, properties?: Record<string, any>): void => {
  if (window.BFG?.amplitude?.enabled) {
    amplitudeService.trackPageView(pageName, properties);
  }
};

/**
 * Track user action
 */
export const trackUserAction = (action: string, properties?: Record<string, any>): void => {
  if (window.BFG?.amplitude?.enabled) {
    amplitudeService.trackUserAction(action, properties);
  }
};

/**
 * Track asset interaction
 */
export const trackAssetInteraction = (action: string, assetId: string, properties?: Record<string, any>): void => {
  if (window.BFG?.amplitude?.enabled) {
    amplitudeService.trackAssetInteraction(action, assetId, properties);
  }
};

/**
 * Track collection interaction
 */
export const trackCollectionInteraction = (action: string, collectionId: string, properties?: Record<string, any>): void => {
  if (window.BFG?.amplitude?.enabled) {
    amplitudeService.trackCollectionInteraction(action, collectionId, properties);
  }
};

/**
 * Track brandfolder interaction
 */
export const trackBrandfolderInteraction = (action: string, brandfolderId: string, properties?: Record<string, any>): void => {
  if (window.BFG?.amplitude?.enabled) {
    amplitudeService.trackBrandfolderInteraction(action, brandfolderId, properties);
  }
};

/**
 * Track search event
 */
export const trackSearch = (query: string, resultsCount?: number, properties?: Record<string, any>): void => {
  if (window.BFG?.amplitude?.enabled) {
    amplitudeService.trackSearch(query, resultsCount, properties);
  }
};

/**
 * Track download event
 */
export const trackDownload = (assetId: string, assetType: string, properties?: Record<string, any>): void => {
  if (window.BFG?.amplitude?.enabled) {
    amplitudeService.trackDownload(assetId, assetType, properties);
  }
};

/**
 * Track upload event
 */
export const trackUpload = (assetType: string, fileSize?: number, properties?: Record<string, any>): void => {
  if (window.BFG?.amplitude?.enabled) {
    amplitudeService.trackUpload(assetType, fileSize, properties);
  }
};
